import {
    collection,
    doc,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    query,
    orderBy,
    serverTimestamp,
} from 'firebase/firestore';
import { db } from './config';
import { uploadToFirebaseStorage } from '../utils/firebaseStorage';

const COLLECTION_NAME = 'collections';

// ─── getCollections ───────────────────────────────────────────────────────────
/**
 * Fetch all collections ordered by creation date (newest first).
 * @returns {Promise<{ collections: Array, error: string|null }>}
 */
export async function getCollections() {
    try {
        const q        = query(collection(db, COLLECTION_NAME), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        const items    = snapshot.docs.map(d => ({
            id:         d.id,
            title:      d.data().title      || '',
            category:   d.data().category   || '',
            imageUrl:   d.data().imageUrl   || '',
            uploadedAt: d.data().uploadedAt || '',
            createdAt:  d.data().createdAt  || null,
        }));
        return { collections: items, error: null };
    } catch (err) {
        console.error('getCollections error:', err);
        return { collections: [], error: err.message };
    }
}

// ─── createCollection ─────────────────────────────────────────────────────────
/**
 * Upload image to Firebase Storage, then save the collection doc to Firestore.
 * @param {{ title: string, category: string, imageFile: File }} data
 * @returns {Promise<{ id: string|null, error: string|null }>}
 */
export async function createCollection({ title, category, imageFile }) {
    if (!title?.trim()) return { id: null, error: 'Title is required.' };
    if (!category)      return { id: null, error: 'Category is required.' };
    if (!imageFile)     return { id: null, error: 'Image is required.' };

    // 1. Upload image
    const { url, error: uploadError } = await uploadToFirebaseStorage(imageFile, 'jg-printing/collections');
    if (uploadError) return { id: null, error: uploadError };

    // 2. Save to Firestore
    try {
        const docRef = await addDoc(collection(db, COLLECTION_NAME), {
            title:      title.trim(),
            category,
            imageUrl:   url,
            uploadedAt: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
            createdAt:  serverTimestamp(),
            updatedAt:  serverTimestamp(),
        });
        return { id: docRef.id, error: null };
    } catch (err) {
        console.error('createCollection error:', err);
        return { id: null, error: err.message };
    }
}

// ─── updateCollection ─────────────────────────────────────────────────────────
/**
 * Update title, category, and optionally the image.
 * If imageFile is provided, re-uploads to Firebase Storage first.
 * @param {string} id
 * @param {{ title: string, category: string, imageFile?: File|null, existingImageUrl?: string }} data
 * @returns {Promise<{ success: boolean, error: string|null }>}
 */
export async function updateCollection(id, { title, category, imageFile, existingImageUrl }) {
    if (!id)            return { success: false, error: 'No collection ID.' };
    if (!title?.trim()) return { success: false, error: 'Title is required.' };
    if (!category)      return { success: false, error: 'Category is required.' };

    try {
        let imageUrl = existingImageUrl || '';

        // Re-upload only if a new file was selected
        if (imageFile instanceof File) {
            const { url, error: uploadError } = await uploadToFirebaseStorage(imageFile, 'jg-printing/collections');
            if (uploadError) return { success: false, error: uploadError };
            imageUrl = url;
        }

        await updateDoc(doc(db, COLLECTION_NAME, id), {
            title:     title.trim(),
            category,
            imageUrl,
            updatedAt: serverTimestamp(),
        });

        return { success: true, error: null };
    } catch (err) {
        console.error('updateCollection error:', err);
        return { success: false, error: err.message };
    }
}

// ─── deleteCollection ─────────────────────────────────────────────────────────
/**
 * Delete a collection document from Firestore.
 * (Firebase Storage image is NOT deleted — delete support is out of scope for this migration.)
 * @param {string} id
 * @returns {Promise<{ success: boolean, error: string|null }>}
 */
export async function deleteCollection(id) {
    if (!id) return { success: false, error: 'No collection ID.' };
    try {
        await deleteDoc(doc(db, COLLECTION_NAME, id));
        return { success: true, error: null };
    } catch (err) {
        console.error('deleteCollection error:', err);
        return { success: false, error: err.message };
    }
}